<template>
  <span
    ref="trigger"
    class="base-tooltip"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @focusin="showTooltip"
    @focusout="hideTooltip"
    @click="handleClick"
  >
    <slot />
    <span class="base-tooltip__announcement" role="status" aria-live="polite" aria-atomic="true">
      {{ feedbackText }}
    </span>

    <Teleport to="body">
      <span
        v-if="visible && hasTooltipContent"
        ref="tooltip"
        class="base-tooltip__content"
        :class="[
          `base-tooltip__content--${position}`,
          `base-tooltip__content--${variant}`,
        ]"
        :style="tooltipStyle"
        role="tooltip"
      >
        <slot name="content">
          {{ feedbackText || text }}
        </slot>
      </span>
    </Teleport>
  </span>
</template>

<script>
export default {
  name: "BaseTooltip",

  props: {
    text: {
      type: String,
      default: "",
    },
    position: {
      type: String,
      default: "top",
      validator: (value) =>
        ["top", "bottom", "left", "right"].includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: [Number, String],
      default: 280,
    },
    variant: {
      type: String,
      default: "dark",
      validator: (value) => ["dark", "light"].includes(value),
    },
    closeOnClick: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      visible: false,
      tooltipStyle: {},
      feedbackText: "",
      feedbackTimer: null,
      feedbackRect: null,
    };
  },

  computed: {
    hasTooltipContent() {
      return !this.disabled && Boolean(this.feedbackText || this.text || this.$slots.content);
    },
  },

  beforeUnmount() {
    clearTimeout(this.feedbackTimer);
    this.removePositionListeners();
  },

  methods: {
    // Короткое подтверждение действия, в том числе после нажатия на touch-экране.
    showFeedback(text, anchorRect = null) {
      clearTimeout(this.feedbackTimer);
      this.feedbackText = text;
      this.feedbackRect = anchorRect;
      this.showTooltip();
      this.feedbackTimer = setTimeout(() => {
        this.feedbackText = "";
        this.feedbackRect = null;
        this.hideTooltip();
      }, 1600);
    },
    showTooltip() {
      if (!this.hasTooltipContent) return;

      this.visible = true;
      this.$nextTick(this.updateTooltipPosition);
      window.addEventListener(
        "scroll",
        this.updateTooltipPosition,
        true
      );
      window.addEventListener("resize", this.updateTooltipPosition);
      document.addEventListener("keydown", this.onKeyDown);
    },

    onKeyDown(event) {
      if (event.key !== "Escape") return;
      clearTimeout(this.feedbackTimer);
      this.feedbackText = "";
      this.feedbackRect = null;
      this.hideTooltip();
    },

    handleClick() {
      if (!this.closeOnClick) return;

      this.visible = false;
      this.removePositionListeners();
    },

    hideTooltip() {
      if (this.feedbackText) return;
      this.visible = false;
      this.removePositionListeners();
    },

    removePositionListeners() {
      document.removeEventListener("keydown", this.onKeyDown);
      window.removeEventListener(
        "scroll",
        this.updateTooltipPosition,
        true
      );
      window.removeEventListener(
        "resize",
        this.updateTooltipPosition
      );
    },

    updateTooltipPosition() {
      const trigger = this.$refs.trigger;
      const tooltip = this.$refs.tooltip;
      if (!trigger || !tooltip) return;

      const triggerRect = this.feedbackRect || trigger.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const gap = 8;
      const coords = this.tooltipCoords(triggerRect, tooltipRect, gap);

      this.tooltipStyle = {
        left: `${this.clamp(coords.left, 8, window.innerWidth - tooltipRect.width - 8)}px`,
        top: `${this.clamp(coords.top, 8, window.innerHeight - tooltipRect.height - 8)}px`,
        maxWidth: `${this.maxWidth}px`,
      };
    },

    tooltipCoords(triggerRect, tooltipRect, gap) {
      const coordsByPosition = {
        top: {
          left: triggerRect.left + (triggerRect.width - tooltipRect.width) / 2,
          top: triggerRect.top - tooltipRect.height - gap,
        },
        bottom: {
          left: triggerRect.left + (triggerRect.width - tooltipRect.width) / 2,
          top: triggerRect.bottom + gap,
        },
        left: {
          left: triggerRect.left - tooltipRect.width - gap,
          top: triggerRect.top + (triggerRect.height - tooltipRect.height) / 2,
        },
        right: {
          left: triggerRect.right + gap,
          top: triggerRect.top + (triggerRect.height - tooltipRect.height) / 2,
        },
      };

      return coordsByPosition[this.position];
    },

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
  },
};
</script>

<style scoped>
.base-tooltip {
  display: inline-flex;
  align-items: center;
}

.base-tooltip :deep(:disabled) {
  pointer-events: none;
}

.base-tooltip__content {
  position: fixed;
  z-index: 2147483647;
  display: inline-block;
  padding: 8px 10px;
  border-radius: 6px;
  background: #1f2937;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
  opacity: 0;
  pointer-events: none;
  white-space: normal;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
  animation: base-tooltip-fade-in 0.14s ease forwards;
}

.base-tooltip__announcement {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.base-tooltip__content::after {
  content: "";
  position: absolute;
  border: 5px solid transparent;
}

.base-tooltip__content--top::after {
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-top-color: #1f2937;
}

.base-tooltip__content--bottom::after {
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-bottom-color: #1f2937;
}

.base-tooltip__content--left::after {
  top: 50%;
  left: 100%;
  transform: translateY(-50%);
  border-left-color: #1f2937;
}

.base-tooltip__content--right::after {
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  border-right-color: #1f2937;
}

.base-tooltip__content--light {
  padding: 12px 14px;
  border: 1px solid #e3e6ee;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(20, 24, 40, 0.2);
  color: #303545;
  font-weight: 400;
}

.base-tooltip__content--light.base-tooltip__content--top::after {
  border-top-color: #ffffff;
}

.base-tooltip__content--light.base-tooltip__content--bottom::after {
  border-bottom-color: #ffffff;
}

.base-tooltip__content--light.base-tooltip__content--left::after {
  border-left-color: #ffffff;
}

.base-tooltip__content--light.base-tooltip__content--right::after {
  border-right-color: #ffffff;
}

@keyframes base-tooltip-fade-in {
  from {
    opacity: 0;
    transform: translateY(2px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
